import { Link, useParams } from "react-router-dom";
import { gossipArticles } from "@/mocks/gossipArticles";
import { gossipCategories } from "@/mocks/gossipCategories";
import Seo from "@/components/base/Seo";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import HotRanking from "@/components/feature/HotRanking";
import NotFound from "@/pages/NotFound";
import { getCategoryName } from "@/utils/category";
import { SITE_URL } from "@/utils/site";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = gossipCategories.find((item) => item.slug === slug);

  if (!category) {
    return <NotFound />;
  }

  const list = gossipArticles.filter((item) => item.categorySlug === category.slug);

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: category.name, item: `${SITE_URL}/category/${category.slug}` },
    ],
  };

  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name} - 瓜料动态`,
    description: category.description,
    url: `${SITE_URL}/category/${category.slug}`,
    inLanguage: "zh-CN",
    mainEntity: {
      "@type": "ItemList",
      itemListElement: list.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.title,
        url: `${SITE_URL}/article/${item.slug}`,
      })),
    },
  };

  return (
    <>
      <Seo
        title={`${category.name} | 瓜料动态${category.tagline}`}
        description={`${category.description} 瓜料动态${category.name}栏目持续更新今日话题，实时吃瓜不错过最新进展。`}
        keywords={`${category.name},瓜料动态${category.name},瓜料动态今日更新,瓜料动态实时吃瓜`}
        path={`/category/${category.slug}`}
        jsonLd={[breadcrumbLd, collectionLd]}
      />
      <Layout>
        <section className="relative overflow-hidden bg-foreground-950">
          <div className="absolute inset-0">
            <img
              src={category.cover}
              alt={`${category.name} - 瓜料动态栏目配图`}
              title={`${category.name} - 瓜料动态最新进展`}
              className="h-full w-full object-cover object-top opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/90 to-foreground-950/60" />
          </div>
          <div className="relative mx-auto max-w-shell px-4 py-12 md:px-6 md:py-16">
            <div className="[&_a]:text-background-300 [&_span]:text-background-100">
              <Breadcrumb items={[{ label: "首页", to: "/" }, { label: category.name }]} />
            </div>
            <span className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-label text-background-50">
              <i className="ri-price-tag-3-line" /> {category.tagline}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">{category.name}</h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
              {category.description}
            </p>
            <p className="mt-4 text-sm text-background-300">共 {list.length} 条相关内容 · 持续更新</p>
          </div>
        </section>

        <div className="mx-auto grid max-w-shell gap-8 px-4 py-12 md:px-6 lg:grid-cols-[1.7fr_1fr]">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950">最新整理</h2>
            <div className="mt-6 space-y-5">
              {list.map((article) => (
                <ArticleCard key={article.slug} article={article} variant="wide" />
              ))}
            </div>

            {list.length === 0 && (
              <p className="mt-6 rounded-lg border border-dashed border-background-300 px-6 py-12 text-center text-sm text-foreground-500">
                这个栏目还在整理中，稍后再来看看。
              </p>
            )}
          </div>

          <aside className="space-y-8">
            <HotRanking limit={8} />
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">其他栏目</h2>
              <ul className="mt-3 space-y-1">
                {gossipCategories
                  .filter((item) => item.slug !== category.slug)
                  .map((item) => (
                    <li key={item.slug}>
                      <Link
                        to={`/category/${item.slug}`}
                        className="flex items-center justify-between rounded-md px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                      >
                        <span>{item.name}</span>
                        <i className="ri-arrow-right-s-line text-foreground-400" />
                      </Link>
                    </li>
                  ))}
              </ul>
            </div>
          </aside>
        </div>
      </Layout>
    </>
  );
}